import React from 'react';
import PropTypes from 'prop-types';
import './Toggle.css';

/**
 * Toggle Component
 * Shared toggle switch component for boolean settings
 *
 * Features:
 * - iOS-style toggle switch
 * - Enabled/disabled state labels
 * - Disabled state support
 * - Full accessibility support
 * - Touch-friendly
 */
const Toggle = ({
  checked,
  onChange,
  label,
  enabledText = 'Enabled',
  disabledText = 'Disabled',
  disabled = false,
  showStateText = true,
  id,
  name,
  className = '',
  ariaLabel,
}) => {
  const toggleId = id || `toggle-${Math.random().toString(36).substr(2, 9)}`;

  const handleChange = (e) => {
    if (!disabled) {
      onChange(e.target.checked);
    }
  };

  const wrapperClasses = ['shared-toggle-wrapper', disabled && 'toggle-disabled', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={toggleId} className="toggle-main-label">
          {label}
        </label>
      )}
      <div className="toggle-control">
        <input
          type="checkbox"
          id={toggleId}
          name={name}
          className="toggle-checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          aria-label={ariaLabel || label}
          aria-checked={checked}
        />
        <label htmlFor={toggleId} className="toggle-switch" aria-hidden="true"></label>
        {showStateText && (
          <span className="toggle-state-text" aria-live="polite">
            {checked ? enabledText : disabledText}
          </span>
        )}
      </div>
    </div>
  );
};

Toggle.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node,
  enabledText: PropTypes.string,
  disabledText: PropTypes.string,
  disabled: PropTypes.bool,
  showStateText: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default Toggle;
